<template>
  <v-dialog
    v-model="show"
    max-width="840"
  >
    <v-card class="product-edit-form-dialog" :loading="isLoading ? 'secondary' : false">
      <v-card-title class="pb-4" style="word-break: keep-all;">
        {{ isExistingProduct ? 'Redigera produkt' : 'Skapa produkt' }}
      </v-card-title>
      <v-card-text>
        <v-container v-if="!!editedProduct">
          <v-row>
            <v-col cols="12" sm="6">
              <v-form ref="productNameTextField" @submit.prevent="saveProduct">
                <v-text-field
                  v-model="editedProduct.name"
                  label="Produktnamn"
                  counter="50"
                  outlined
                  :error-messages="productNameErrorMessages"
                ></v-text-field>
              </v-form>
            </v-col>
            <v-col cols="12" sm="6">
              <v-select
                v-model="editedProduct.product_group_id"
                :items="productGroupItems"
                label="Produktgrupp"
                outlined
                item-value="id"
                item-text="label"
                :error-messages="productGroupIdErrorMessages"
                :persistent-hint="editedProduct.product_group_id > 0"
                :hint="productGroupHint"
              >
                <template v-slot:item="{ item }">
                  <div class="d-flex flex-column py-2">
                    <span>{{ item.name }}</span>
                    <span class="text-caption grey--text">{{ item.accountAndVat }}</span>
                  </div>
                </template>
              </v-select>
            </v-col>
            <v-col cols="12" sm="6">
              <v-text-field
                v-model="priceInput"
                label="Pris inkl. moms"
                :disabled="!canEditPrice"
                clearable
                outlined
                hint="Lämna fältet tomt om varan saknar ett bestämt pris"
                persistent-hint
                append-icon="coi-currency-sek"
                type="number"
                hide-spin-buttons
                :error-messages="priceErrorMessages"
              ></v-text-field>
            </v-col>
            <v-col cols="12" sm="6" class="mt--2">
              <label class="text-caption" style="font-size: 16px;">Produktfärg</label>
              <color-picker-menu
                v-model="editedProduct.gui_color"
                show-vuetify-colors
                :show-hex-colors="false"
                class="mt-1"
              ></color-picker-menu>
            </v-col>
            <v-col cols="12">
              <label class="text-caption" style="width: 100%; font-size: 16px;">Streckkod</label>
              <div class="d-flex align-center">
                <v-icon left>
                  mdi-barcode
                </v-icon>
                <span v-text="barcodeText" :class="{ 'text-button': hasBarcode }"></span>
                <div class="ml-3">
                  <v-btn
                    v-if="hasBarcode"
                    text
                    color="error"
                    @click="deleteBarcode"
                  >
                    Ta bort streckkod
                  </v-btn>
                  <v-btn
                    v-else
                    color="success"
                    text
                    @click="openBarcodeInput"
                  >
                    Lägg till streckkod
                  </v-btn>
                </div>
              </div>
            </v-col>
            <v-col cols="12">
              <v-switch
                v-model="editedProduct.should_have_delivery_date"
                label="Produkten ska alltid ha ett leveransdatum"
                hide-details
                color="secondary"
              ></v-switch>
            </v-col>
            <v-col v-if="editedProduct.should_have_delivery_date" cols="12" sm="6">
              <v-select
                v-model="editedProduct.default_delivery_date"
                :items="defaultDeliveryDateOptions"
                label="Förvalt leveransdatum"
                outlined
                item-value="value"
                item-text="text"
                :rules="[v => !!v || 'Välj ett leveransdatum']"
              ></v-select>
            </v-col>
          </v-row>
          <template v-if="productUsedOnSettings.length">
            <v-alert type="info" text class="mt-8 mb-0">
              <div class="pb-2">Observera att denna produkt används i följande inställningar</div>
              <div
                v-for="setting in productUsedOnSettings"
                :key="setting.id"
                class="mt-3"
              >
                <div class="d-flex primary--text">
                  <v-icon color="primary" class="mr-3">mdi-cogs</v-icon>
                  <div class="font-weight-medium my-auto">{{ setting.text }}</div>
                  <template v-if="setting.product_price_affects_setting">
                    <div class="ml-2 my-auto align-center text-caption">
                      (produktens pris påverkar inställningens pris)
                    </div>
                  </template>
                  <template v-else>
                    <div class="ml-2 my-auto align-center text-caption">
                      (produktens pris påverkar <strong>inte</strong> inställningens pris - priset i inställningen används istället)
                    </div>
                  </template>
                </div>
              </div>
            </v-alert>
          </template>
        </v-container>
      </v-card-text>
      <v-card-actions class="pa-4">
        <v-btn
          color="error"
          text
          @click="show = false"
          :disabled="isLoading"
        >
          Avbryt
        </v-btn>
        <v-spacer></v-spacer>
        <v-btn
          color="success"
          @click="saveProduct"
          :loading="isLoading"
        >
          {{ isExistingProduct ? 'Spara ändringar' : 'Skapa produkt' }}
        </v-btn>
      </v-card-actions>
    </v-card>
    <barcode-input
      v-model="barcodeInput"
      :show="showBarcodeInput"
      @close="closeBarcodeInput"
    ></barcode-input>
  </v-dialog>
</template>

<script>
import { mapState } from 'vuex'

import BarcodeInput from './BarcodeInput.vue'
import ColorPickerMenu from '@/components/onboarding/ColorPickerMenu.vue'

export default {
  name: 'ProductEditFormDialog',
  components: {
    BarcodeInput,
    ColorPickerMenu
  },
  data: () => ({
    barcodeInput: '',
    showBarcodeInput: false,
    hardValidation: false,
    isLoading: false
  }),
  computed: {
    ...mapState(
      {
        productGroups: state => state.product.productGroups,
        products: state => state.product.products,
        productUsedOnSettings: state => state.product.formDialog.productUsedOnSettings
      }
    ),
    show: {
      get () {
        return this.$store.state.product.formDialog.show
      },
      set (value) {
        this.$store.commit('product/SET_SHOW_FORM_DIALOG', value)
      }
    },
    isExistingProduct: function () {
      return !!this.editedProduct && !!this.editedProduct.id
    },
    editedProduct: {
      get () {
        return this.$store.state.product.formDialog.editedProduct
      },
      set (value) {
        this.$store.commit('product/SET_EDITED_PRODUCT', value)
      }
    },
    priceInput: {
      get () {
        if (this.editedProduct.price === '' || this.editedProduct.price === null || this.editedProduct.price === undefined) {
          return ''
        }
        return this.editedProduct.price / 100
      },
      set (value) {
        this.editedProduct.price = value === '' || value === null ? null : Math.round(Math.abs(value * 100))
      }
    },
    productGroupItems: function () {
      return this.productGroups.map(productGroup => {
        const taxName = productGroup.tax ? productGroup.tax.name : ''
        const accountNumber = productGroup.account ? productGroup.account.account_number : ''
        const accountName = productGroup.account ? productGroup.account.name : ''
        const accountAndVat = [accountNumber, accountName, taxName].filter(Boolean).join(' · ')
        return {
          id: productGroup.id,
          name: productGroup.name,
          label: productGroup.name,
          accountAndVat
        }
      })
    },
    defaultDeliveryDateOptions: function () {
      return [
        { value: 'sales_date', text: 'Försäljningsdatum' },
        { value: 'check_in_date', text: 'Incheckningsdatum' },
        { value: 'check_out_date', text: 'Utcheckningsdatum' }
      ]
    },
    productGroupHint: function () {
      if (this.editedProduct.product_group_id) {
        const productGroup = this.productGroups.find(pg => pg.id === this.editedProduct.product_group_id)
        if (productGroup) {
          const parts = []
          if (productGroup.account) {
            parts.push(productGroup.account.account_number)
            parts.push(productGroup.account.name)
          }
          if (productGroup.tax) {
            parts.push(productGroup.tax.name)
          }
          return parts.filter(Boolean).join(' · ')
        }
      }
      return ''
    },
    canEditPrice: function () {
      if (!this.productUsedOnSettings?.length) {
        return true
      }
      // Om product_price_affects_setting är false innebär det i praktiken att priset sätts från inställningen istället, så tillåter inte redigering av priset på produkten i det fallet
      return !this.productUsedOnSettings.some(setting => !setting.product_price_affects_setting)
    },
    hasBarcode: function () {
      return this.editedProduct && this.editedProduct.barcode
    },
    barcodeText: function () {
      if (this.hasBarcode) {
        return this.editedProduct.barcode
      }
      return 'Varan saknar streckkod'
    },
    productGroupIdErrorMessages: function () {
      if (!this.editedProduct) {
        return []
      }
      if (this.hardValidation && !this.editedProduct.product_group_id) {
        return ['Välj en produktgrupp till produkten']
      }
      return []
    },
    priceErrorMessages: function () {
      if (!this.editedProduct) {
        return []
      }
      const price = this.editedProduct.price
      if (price === null || price === '' || price === undefined) {
        return []
      }
      if (price < 0) {
        return ['Priset kan inte vara negativt']
      }
      if (price > 1000000000) {
        return ['Priset kan inte vara mer än 10 000 000 kr']
      }
      return []
    },
    productNameErrorMessages: function () {
      if (!this.editedProduct) {
        return []
      }
      const editedProductName = this.editedProduct.name.toLowerCase()
      if (editedProductName.length > 50) {
        return ['Max 50 tecken']
      } else if (this.hardValidation && editedProductName.length === 0) {
        return ['Ange ett namn']
      }
      return []
    }
  },
  methods: {
    openBarcodeInput: function () {
      this.barcodeInput = this.editedProduct.barcode ? this.editedProduct.barcode : ''
      this.showBarcodeInput = true
    },
    closeBarcodeInput: function () {
      this.showBarcodeInput = false
      this.editedProduct.barcode = this.barcodeInput
    },
    deleteBarcode: function () {
      this.editedProduct.barcode = ''
    },
    validate: function () {
      this.hardValidation = true
      const productNameValid = this.productNameErrorMessages.length === 0
      const productGroupValid = this.productGroupIdErrorMessages.length === 0
      const priceValid = this.priceErrorMessages.length === 0
      return productNameValid && productGroupValid && priceValid
    },
    saveProduct: function () {
      if (this.validate()) {
        this.$store.dispatch('product/saveProduct')
      }
    }
  },
  watch: {
    show: function () {
      if (!this.show) {
        this.hardValidation = false
      }
    },
    'editedProduct.should_have_delivery_date': function (value) {
      if (!value) {
        this.editedProduct.default_delivery_date = null
      }
    }
  }
}
</script>
